/**
 * Reads "25.1972, 55.2744" — what Google Maps copies when you right-click a point — or a
 * maps URL containing "@lat,lng" or "q=lat,lng". Returns null when it is neither.
 */
export function parseCoordinates(text: string): { latitude: number; longitude: number } | null {
  const match = /(-?\d{1,3}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)/.exec(text)
  if (!match) return null
  const latitude = Number(match[1])
  const longitude = Number(match[2])
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null
  return { latitude, longitude }
}

export interface Position {
  latitude: number
  longitude: number
  accuracy: number
}

/** The browser's current position, as a promise, with errors in words a person can act on. */
export function currentPosition(): Promise<Position> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('This browser cannot share its location.'))
      return
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({
        latitude: Number(position.coords.latitude.toFixed(7)),
        longitude: Number(position.coords.longitude.toFixed(7)),
        accuracy: Math.round(position.coords.accuracy),
      }),
      (error) => reject(new Error(
        error.code === error.PERMISSION_DENIED
          ? 'Location access is blocked. Allow location for this site in your browser settings and try again.'
          : error.code === error.TIMEOUT
            ? 'Finding your location took too long. Try again, ideally near a window or outdoors.'
            : 'Your location could not be found. Check that location services are on.',
      )),
      { enableHighAccuracy: true, timeout: 20_000, maximumAge: 0 },
    )
  })
}

export function formatMetres(metres: number | null | undefined): string {
  if (metres == null) return '—'
  return metres >= 1000 ? `${(metres / 1000).toFixed(1)} km` : `${metres} m`
}
